import { useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { currentLocationSelector, onboardingSelector } from '../redux-store/OnboardingSlice';
import { createLogger } from '../services/Logger';
import { NavigationService } from '../services/nav/NavigationService';

const log = createLogger("useOnboardingNavigation");

export const useOnboardNavAuth = (currentPageSlug?: string) => {
  const navigate = useNavigate();

  const currentLocation = useSelector(currentLocationSelector);
  const { error } = useSelector(onboardingSelector);

  // Navigation wrapper functions that actually navigate
  const handleNext = useCallback(
    async (state?: any) => {
      try {
        // Get the current page manager to determine next step
        const currentPageManager = NavigationService.navStepManagerBySlug(
          currentPageSlug as any
        );

        if (!currentPageManager) {
          log.error("handleNext: Current page manager not found");
          return false;
        }

        // Get the next page URL
        const nextUrl = NavigationService.getNextPageUrl(
          currentPageSlug || "",
          currentPageManager
        );

        if (!nextUrl) {
          log.error("handleNext: Next page URL not found");
          return false;
        }

        // Navigate to the next page
        navigate(nextUrl);

        return true;
      } catch (error) {
        log.error("handleNext: Navigation failed", error);
        return false;
      }
    },
    [navigate, currentPageSlug]
  );

  const handlePrev = useCallback(
    async (state?: any) => {
      try {
        // Get the current page manager to determine previous step
        const currentPageManager = NavigationService.navStepManagerBySlug(
          currentPageSlug as any
        );
        if (!currentPageManager) {
          log.error("handlePrev: Current page manager not found");
          return false;
        }

        // Get the previous page URL
        const prevUrl =
          NavigationService.getPreviousPageUrl(currentPageManager);
        if (!prevUrl) {
          log.error("handlePrev: Previous page URL not found");
          return false;
        }

        // Navigate to the previous page
        navigate(prevUrl);

        return true;
      } catch (error) {
        log.error("handlePrev: Navigation failed", error);
        return false;
      }
    },
    [navigate, currentPageSlug]
  );

  return {
    // Current state
    currentLocation,
    error,

    // Navigation middleware
    handleNext,
    handlePrev,
  };
};
